/**
 * Global Search – useSearch hook
 *
 * Debounces the search query (300 ms) and triggers typeahead + full-text
 * search automatically. Provides keyboard navigation helpers.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from '@hooks/useDebounce';
import { useSearchStore } from '../store/searchStore';
import type { SearchEntityType, TypeaheadSuggestion } from '../types';

const DEBOUNCE_MS = 300;

/** Map entity types to detail routes. */
function getEntityRoute(entityType: SearchEntityType, id: string): string {
  switch (entityType) {
    case 'customer':
      return `/customers/${id}`;
    case 'account':
      return `/loans/accounts/${id}`;
    case 'transaction':
      return `/transactions`;
    case 'loan':
      return `/loans/applications/${id}`;
    case 'document':
      return `/search?doc=${id}`;
    default:
      return '/search';
  }
}

export function useSearch() {
  const navigate = useNavigate();
  const abortRef = useRef(false);

  const {
    query,
    setQuery,
    filters,
    suggestions,
    suggestionsLoading,
    fetchSuggestions,
    clearSuggestions,
    fetchResults,
    addRecentSearch,
    highlightedIndex,
    setHighlightedIndex,
    total,
  } = useSearchStore();

  const debouncedQuery = useDebounce(query, DEBOUNCE_MS);

  // Fetch typeahead whenever debounced query changes
  useEffect(() => {
    if (abortRef.current) {
      abortRef.current = false;
      return;
    }
    if (debouncedQuery.trim().length >= 2) {
      fetchSuggestions(debouncedQuery);
    } else {
      clearSuggestions();
    }
  }, [debouncedQuery, fetchSuggestions, clearSuggestions]);

  /** Execute a full search and navigate to the results page. */
  const executeSearch = useCallback(
    async (q?: string) => {
      const searchQuery = q ?? query;
      if (!searchQuery.trim()) return;
      abortRef.current = true;
      clearSuggestions();
      setHighlightedIndex(-1);
      await fetchResults(searchQuery, filters);
      addRecentSearch(searchQuery, total);
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    },
    [query, filters, fetchResults, addRecentSearch, navigate, clearSuggestions, setHighlightedIndex, total],
  );

  /** Navigate to a specific search result entity. */
  const navigateToResult = useCallback(
    (suggestion: TypeaheadSuggestion) => {
      abortRef.current = true;
      clearSuggestions();
      setHighlightedIndex(-1);
      navigate(getEntityRoute(suggestion.entityType, suggestion.id));
    },
    [navigate, clearSuggestions, setHighlightedIndex],
  );

  /** Handle keyboard events on the search input. */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const count = suggestions.length;
      if (!count) {
        if (e.key === 'Enter') {
          executeSearch();
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(highlightedIndex < count - 1 ? highlightedIndex + 1 : 0);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(highlightedIndex > 0 ? highlightedIndex - 1 : count - 1);
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < count) {
            navigateToResult(suggestions[highlightedIndex]);
          } else {
            executeSearch();
          }
          break;
        case 'Escape':
          clearSuggestions();
          setHighlightedIndex(-1);
          break;
      }
    },
    [suggestions, highlightedIndex, setHighlightedIndex, executeSearch, navigateToResult, clearSuggestions],
  );

  return {
    query,
    setQuery,
    debouncedQuery,
    suggestions,
    suggestionsLoading,
    highlightedIndex,
    handleKeyDown,
    executeSearch,
    navigateToResult,
    clearSuggestions,
  };
}
